import { useState, useEffect } from 'preact/hooks'
import { getProcessByReturnToken } from '@/services/api'
import { ProcessDtoType, type ProcessDto } from '@cencert/mob-signature'
import { useQueryParam } from './useQueryParams'
import {
  saveAccessTokenType,
  saveErrorMessage,
  saveExpirationTime,
  saveIsMObywatelProcess,
  saveProcessId,
  saveWebReturnUrl,
} from '@/services/session'
import { useLocation } from 'wouter-preact'
import { saveProcessState } from '@/services/navigation'
import { useConfig } from '@/services/config'

export interface ReturnTokenProcessing {
  isProcessing: boolean
  isProcessed: boolean
  processData: ProcessDto | null
}

export function useReturnToken(): ReturnTokenProcessing {
  const returnToken = useQueryParam('returnToken')
  const [isProcessing, setIsProcessing] = useState(!!returnToken)
  const [isProcessed, setIsProcessed] = useState(false)
  const [processData, setProcessData] = useState<ProcessDto | null>(null)
  const [, setLocation] = useLocation()
  const { setExpirationTime } = useConfig()

  useEffect(() => {
    if (!returnToken) {
      setIsProcessed(true)
      return
    }

    const processReturnToken = async () => {
      await getProcessByReturnToken(returnToken, {
        onSuccess: (response: ProcessDto) => {
          setProcessData(response)
          setIsProcessed(true)
          saveProcessId(response.id)
          saveIsMObywatelProcess(true)
          saveAccessTokenType(response.type ?? ProcessDtoType.DOCUMENT)
          saveWebReturnUrl(response.webReturnUrl ?? '')

          saveProcessState({
            currentStep: 'summary',
            allowedNextSteps: ['waiting-for-signature'],
            completedSteps: ['terms', 'add-document', 'qr-code'],
          })

          if (response?.expirationTime) {
            saveExpirationTime(response.expirationTime)
            setExpirationTime(response.expirationTime)
          }

          const url = new URL(window.location.href)
          url.searchParams.delete('returnToken')
          window.history.replaceState({}, '', url.toString())
          setIsProcessing(false)
        },
        onError: error => {
          const errorMessage = error.response?.data?.detail
          if (errorMessage) {
            saveErrorMessage(errorMessage)
          }
          setIsProcessing(false)
          setLocation('/process-error')
        },
      })
    }

    processReturnToken()
  }, [])

  return {
    isProcessing,
    isProcessed,
    processData,
  }
}
